import {Injectable} from '@angular/core';
import {
  CategoryCode,
  Coverage,
  Duration,
  HUMANIA_EMMA_V2_CANCER_RIDER_COVERAGE_AMOUNT_INTERVAL_EMMA_APP,
  HUMANIA_EMMA_V2_CANCER_RIDER_MAX_AGE,
  HUMANIA_EMMA_V2_CANCER_RIDER_MAX_COVERAGE,
  HUMANIA_EMMA_V2_CANCER_RIDER_MIN_COVERAGE,
  HUMANIA_EMMA_V2_CHILD_RIDER_COVERAGES,
  HUMANIA_EMMA_V2_MAX_TERM_AGE,
  HUMANIA_EMMA_V2_MIN_TERM_COVERAGE,
  HUMANIA_EMMA_V2_MIN_WHOLE_COVERAGE,
  HUMANIA_EMMA_V2_PROVIDER,
  RiderType,
} from '@emma-labs/shared/typescript';

@Injectable({
  providedIn: 'root',
})
export class CoverageRecommendationService {
  getRecommendedCoverage(
    age: number,
    temporaryNeeds: number,
    permanentNeeds: number,
    needs: number, // TODO rm with IS_NEW_NEW_QUOTE_PAGE_NEW_ENABLED clean up
    cancerNeeds: number,
    numberOfChildren: number,
    categoryCode: CategoryCode,
    isPriceToRevenueRatioTooHigh: boolean,
    youngestChildAgeInYears?: number,
    isNewQuotePageEnabled = false,
  ): Coverage {
    const category = HUMANIA_EMMA_V2_PROVIDER.categories.find((c) => c.code === categoryCode);
    if (!category) {
      throw new Error('Category not defined for this provider');
    }

    if (!isNewQuotePageEnabled && age > HUMANIA_EMMA_V2_MAX_TERM_AGE) {
      return {
        lifeInsurances: {
          [Duration.P100]: Math.max(
            Math.min(needs, category.maxCoverage),
            HUMANIA_EMMA_V2_MIN_WHOLE_COVERAGE,
          ),
        },
      };
    }

    let termLifeDuration: Duration | undefined = Duration.T10;
    if (numberOfChildren > 0 && youngestChildAgeInYears == null) {
      throw new Error('Missing youngest child age for smart recommendation.');
    }

    const conditionForT20 =
      (!isPriceToRevenueRatioTooHigh &&
        numberOfChildren > 0 &&
        youngestChildAgeInYears! <= 10 &&
        age <= 60) ||
      (numberOfChildren === 0 && age <= 40);
    if (conditionForT20) {
      termLifeDuration = Duration.T20;
    }

    if (isNewQuotePageEnabled) {
      let termLifeCoverageAmount = 0;
      const wholeLifeCoverageAmount = Math.max(
        Math.min(permanentNeeds, category.maxCoverage),
        HUMANIA_EMMA_V2_MIN_WHOLE_COVERAGE,
      );

      if (age <= HUMANIA_EMMA_V2_MAX_TERM_AGE) {
        termLifeCoverageAmount = Math.max(
          Math.min(temporaryNeeds, category.maxCoverage),
          HUMANIA_EMMA_V2_MIN_TERM_COVERAGE,
        );
      }

      const lifeInsurances = {
        ...(termLifeCoverageAmount > 0 && {
          [termLifeDuration]: termLifeCoverageAmount,
        }),
        [Duration.P100]: wholeLifeCoverageAmount,
      };
      const coverage: Coverage = {lifeInsurances};

      let childRiderCoverageAmount: number | undefined;
      if (numberOfChildren > 0) {
        childRiderCoverageAmount =
          HUMANIA_EMMA_V2_CHILD_RIDER_COVERAGES[HUMANIA_EMMA_V2_CHILD_RIDER_COVERAGES.length - 1];
        const coverageValues = [0, ...HUMANIA_EMMA_V2_CHILD_RIDER_COVERAGES];

        childRiderCoverageAmount = coverageValues
          .sort((a, b) => b - a)
          .find((value) => termLifeCoverageAmount + wholeLifeCoverageAmount >= value * 2);

        if (childRiderCoverageAmount && childRiderCoverageAmount > 0) {
          coverage.riders = [{type: RiderType.CHILD, coverage: childRiderCoverageAmount}];
        }
      }

      let cancerRiderCoverageAmount = 0;
      const roundedCancerNeeds =
        Math.ceil(cancerNeeds / HUMANIA_EMMA_V2_CANCER_RIDER_COVERAGE_AMOUNT_INTERVAL_EMMA_APP) *
        HUMANIA_EMMA_V2_CANCER_RIDER_COVERAGE_AMOUNT_INTERVAL_EMMA_APP;
      cancerRiderCoverageAmount = Math.max(
        Math.min(roundedCancerNeeds, HUMANIA_EMMA_V2_CANCER_RIDER_MAX_COVERAGE),
        HUMANIA_EMMA_V2_CANCER_RIDER_MIN_COVERAGE,
      );

      if (age <= HUMANIA_EMMA_V2_CANCER_RIDER_MAX_AGE && cancerRiderCoverageAmount > 0) {
        coverage.riders = [
          ...(coverage.riders || []),
          {type: RiderType.CANCER, coverage: cancerRiderCoverageAmount},
        ];
      }

      return coverage;
    }

    return {
      lifeInsurances: {
        [termLifeDuration]: Math.max(
          Math.min(needs, category.maxCoverage),
          HUMANIA_EMMA_V2_MIN_TERM_COVERAGE,
        ),
      },
    };
  }
}
